import { useCallback, useEffect, useRef, useState } from "react";
import { useTour, TOUR_TOTAL_STEPS } from "@contexts/TourContext";
import type {
  BoardTourViewModel,
  TourDemoCardPlacement,
  TourStep,
} from "./BoardTour.ViewModel.types";

/** The demo card goes under this, so it sits where a first card would. */
export const DEMO_CARD_ANCHOR_SELECTOR = '[data-tour="add-task"]';
const DEMO_CARD_GAP = 8;

export const TOUR_STEPS: TourStep[] = [
  { selector: '[data-tour="header-nav"]',           side: "below", titleKey: "tour.step1.title", bodyKey: "tour.step1.body" },
  { selector: '[data-tour="sidebar-connections"]',  side: "below", titleKey: "tour.step2.title", bodyKey: "tour.step2.body" },
  { selector: '[data-tour="sidebar-add-connection"]', side: "after", titleKey: "tour.step3.title", bodyKey: "tour.step3.body" },
  { selector: '[data-tour="sidebar-metrics"]',      side: "after", titleKey: "tour.step4.title", bodyKey: "tour.step4.body" },
  // Board steps point at the first match: the first column, its first card.
  // Never "below" or "above" a whole column — it is as tall as the board, so the
  // popover has no room on either side and its header lands off screen.
  { selector: '[data-tour="column-header"]',        side: "below", titleKey: "tour.step5.title", bodyKey: "tour.step5.body" },
  { selector: '[data-tour="column-collapse"]',      side: "below", titleKey: "tour.step6.title", bodyKey: "tour.step6.body" },
  { selector: '[data-tour="add-task"]',             side: "after", titleKey: "tour.step7.title", bodyKey: "tour.step7.body" },
  { selector: '[data-tour="task-card"]',            side: "after", titleKey: "tour.step8.title", bodyKey: "tour.step8.body", demoWhenMissing: true },
  { selector: '[data-tour="task-source-icon"]',     side: "after", titleKey: "tour.step9.title", bodyKey: "tour.step9.body", demoWhenMissing: true },
  { selector: '[data-tour="check-now"]',            side: "below", titleKey: "tour.step10.title", bodyKey: "tour.step10.body" },
  { selector: '[data-tour="view-options"]',         side: "below", titleKey: "tour.step11.title", bodyKey: "tour.step11.body" },
  { selector: '[data-tour="copy-board"]',           side: "below", titleKey: "tour.step12.title", bodyKey: "tour.step12.body" },
  { selector: '[data-tour="ai-assistant"]',         side: "below", titleKey: "tour.step13.title", bodyKey: "tour.step13.body" },
  { selector: '[data-tour="board-notifications"]',  side: "below", titleKey: "tour.step14.title", bodyKey: "tour.step14.body" },
];

export const useBoardTourViewModel = (): BoardTourViewModel => {
  const { isActive, currentStep, totalSteps, nextStep, skipTour, completeTour } = useTour();

  const next = useCallback(() => {
    if (currentStep >= TOUR_TOTAL_STEPS) {
      completeTour();
    } else {
      nextStep();
    }
  }, [currentStep, nextStep, completeTour]);

  const [targetElement, setTargetElement] = useState<Element | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [demoCardPlacement, setDemoCardPlacement] =
    useState<TourDemoCardPlacement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const updateRect = useCallback((el: Element) => {
    setTargetRect(el.getBoundingClientRect());
  }, []);

  useEffect(() => {
    if (!isActive) {
      setTargetElement(null);
      setTargetRect(null);
      setDemoCardPlacement(null);
      return;
    }

    const step = TOUR_STEPS[currentStep - 1];
    if (!step) return;

    if (!step.demoWhenMissing && demoCardPlacement) {
      // This effect runs again once it is gone, and finds the step's target then.
      setDemoCardPlacement(null);
      return;
    }

    const el = document.querySelector(step.selector);

    if (!el && step.demoWhenMissing && !demoCardPlacement) {
      const anchor = document.querySelector(DEMO_CARD_ANCHOR_SELECTOR);
      if (!anchor) {
        next();
        return;
      }
      // Runs again once the demo card has rendered, and finds it then.
      const rect = anchor.getBoundingClientRect();
      setDemoCardPlacement({
        top: rect.bottom + DEMO_CARD_GAP,
        left: rect.left,
        width: rect.width,
      });
      return;
    }

    setTargetElement(el);

    if (!el) {
      setTargetRect(null);
      return;
    }

    updateRect(el);

    observerRef.current?.disconnect();
    const ro = new ResizeObserver(() => updateRect(el));
    ro.observe(el);
    observerRef.current = ro;

    const onScroll = () => updateRect(el);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [isActive, currentStep, updateRect, next, demoCardPlacement]);

  // Clean up observer when tour becomes inactive
  useEffect(() => {
    if (!isActive) {
      observerRef.current?.disconnect();
    }
  }, [isActive]);

  return {
    isActive,
    currentStep,
    totalSteps,
    targetElement,
    targetRect,
    isLastStep: currentStep >= TOUR_TOTAL_STEPS,
    demoCardPlacement,
    steps: TOUR_STEPS,
    next,
    skip: skipTour,
    complete: completeTour,
  };
};
